import {
  FiltersForUrl,
  FilterStore,
  FilterStorePeriod,
} from '@/pages/OrdersHistory/components/OrderHistoryFilters/types'
import { FiltersForUrlTypeMap } from '@/pages/OrdersHistory/components/OrderHistoryFilters/constants'
import { useMemo } from 'react'
import {
  getParsedDateFromUrl,
  getSearchFilter,
  validateFilterOptions,
  validatePage,
  validatePageSize,
} from '@/resources/utils'
import { UrlFilterValues } from './types'
import { useUrlFilters } from '@/hooks/url'

const useHistoryUrlFilters = (filtersStore: FilterStore): FilterStore => {
  const getUrlFilters = useUrlFilters<FiltersForUrl>({
    typeMap: FiltersForUrlTypeMap,
  })

  const urlFiltersResult = useMemo(() => getUrlFilters(), [getUrlFilters])

  const {
    period: urlPeriod,
    search: searchValue,
    page: urlPage,
    pageSize: urlPageSize,
    ...urlOptions
  } = urlFiltersResult

  const initialStore: FilterStore = useMemo(
    () => ({
      ...filtersStore,
      ...getValidatedFilters(urlOptions as UrlFilterValues),
      search: getSearchFilter((searchValue as string | undefined) ?? ''),
      period: getPeriod(
        filtersStore.period,
        urlPeriod as [string, string] | undefined
      ),
      page: getValidatedPage(
        (urlPage as number | undefined) ?? filtersStore.page
      ),
      pageSize: getValidatedPageSize(
        (urlPageSize as number | undefined) ?? filtersStore.pageSize
      ),
    }),
    [filtersStore, searchValue, urlOptions, urlPeriod, urlPage, urlPageSize]
  )

  return initialStore
}

const getPeriod = (storePeriod: FilterStorePeriod, urlPeriod?: string[]) => {
  const startDate = urlPeriod?.[0]
  const endDate = urlPeriod?.[1]

  const parsedStart = startDate
    ? getParsedDateFromUrl(startDate)
    : storePeriod.start
  const parsedEnd = endDate ? getParsedDateFromUrl(endDate) : storePeriod.end

  return {
    start: parsedStart ?? storePeriod.start,
    end: parsedEnd ?? storePeriod.end,
  }
}

const getValidatedPage = (urlPage?: number) => validatePage(urlPage)

const getValidatedPageSize = (urlPageSize?: number) =>
  validatePageSize(urlPageSize)

const getValidatedFilters = (urlFilters: UrlFilterValues) => {
  const validated: UrlFilterValues = {}

  Object.entries(urlFilters).forEach(([key, value]) => {
    const prop = key
    validated[prop] = validateFilterOptions(prop, value)
  })

  return validated
}

export default useHistoryUrlFilters
